/**
 * Reading a template agent's stored switches, with the definition's defaults
 * where nothing is stored. Shared by the settings sections and the page.
 */

import type { AgentSettings } from '../../../lib/agentSettingsService';
import type { AgentDefinition, AgentEventKey } from '../../../lib/ai/agentDefinitions';

/** The stored switch group, with the definition's defaults where nothing is stored. */
export function stored<T extends object>(defaults: T, raw: Record<string, unknown> | undefined): T {
  return { ...defaults, ...(raw ?? {}) } as T;
}

/** Which evidence sources are on: the settings row wins, else the definition's default. */
export function resolveSources(def: AgentDefinition, config: AgentSettings | null): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const source of def.evidenceSources) {
    const v = config?.evidenceSources?.[source.key];
    out[source.key] = source.available && (typeof v === 'boolean' ? v : source.defaultEnabled);
  }
  return out;
}

export interface ResolvedTriggers {
  manualSingle: boolean;
  manualBulk: boolean;
  runNow: boolean;
  events: AgentEventKey[];
  scheduleMinutes: number | null;
  debounceSeconds: number;
  taskAssigned: boolean;
}

export function resolveTriggers(def: AgentDefinition, config: AgentSettings | null): ResolvedTriggers {
  const raw = (config?.triggers ?? {}) as Partial<ResolvedTriggers>;
  return {
    manualSingle: typeof raw.manualSingle === 'boolean' ? raw.manualSingle : def.triggers.manualSingle,
    manualBulk: typeof raw.manualBulk === 'boolean' ? raw.manualBulk : def.triggers.manualBulk,
    runNow: typeof raw.runNow === 'boolean' ? raw.runNow : def.triggers.runNow,
    events: Array.isArray(raw.events) ? raw.events.filter((e) => def.supportedEvents.includes(e)) : def.triggers.events,
    scheduleMinutes: typeof raw.scheduleMinutes === 'number' && raw.scheduleMinutes >= 5 ? raw.scheduleMinutes : (raw.scheduleMinutes === null ? null : def.triggers.scheduleMinutes),
    debounceSeconds: typeof raw.debounceSeconds === 'number' ? raw.debounceSeconds : def.triggers.debounceSeconds,
    taskAssigned: typeof raw.taskAssigned === 'boolean' ? raw.taskAssigned : def.supportedEvents.includes('task_assigned'),
  };
}
